"use client";

// Before/after comparison chart for the "Effekttoppar" bento card.
// Driven by the real peak-shaving dataset (15-min resolution):
//   SOC (green)        – battery level %
//   Peak (red)         – electricity usage
//   GridImpact (blue)  – grid impact
// The divider sits in the middle by default and is always visible: red
// "before" to its left, green + blue "after" to its right. Drag the handle
// (or anywhere on the chart) left/right to wipe between the two.

import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";

type Row = { SOC: number; Peak: number; GridImpact: number };

// SOC (battery) is intentionally kept in the upper half: peak shaving only
// draws the battery down to ~50%, never empties it, which is closer to how a
// real reserve-protecting strategy behaves.
const DATA: Row[] = [
  { SOC: 50, Peak: 6.5, GridImpact: 6.5 },
  { SOC: 53.1, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 56.3, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 59.4, Peak: 7.5, GridImpact: 7.5 },
  { SOC: 62.5, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 65.6, Peak: 7.3, GridImpact: 7.3 },
  { SOC: 68.8, Peak: 7.1, GridImpact: 7.1 },
  { SOC: 71.9, Peak: 6.7, GridImpact: 6.7 },
  { SOC: 70.6, Peak: 7.4, GridImpact: 7.4 },
  { SOC: 78.1, Peak: 6.6, GridImpact: 6.6 },
  { SOC: 81.3, Peak: 11.8, GridImpact: 11.8 },
  { SOC: 84.4, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 87.5, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 87.3, Peak: 7.6, GridImpact: 7.6 },
  { SOC: 93.8, Peak: 7.1, GridImpact: 7.1 },
  { SOC: 96.9, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 100, Peak: 7.3, GridImpact: 7.3 },
  { SOC: 100, Peak: 6.7, GridImpact: 6.7 },
  { SOC: 100, Peak: 7.5, GridImpact: 7.5 },
  { SOC: 98.9, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 100, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 100, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 100, Peak: 13.5, GridImpact: 13.5 },
  { SOC: 100, Peak: 8.2, GridImpact: 8.2 },
  { SOC: 100, Peak: 12.8, GridImpact: 7.5 },
  { SOC: 95.8, Peak: 15.3, GridImpact: 9.8 },
  { SOC: 92.8, Peak: 17.6, GridImpact: 11.2 },
  { SOC: 84.7, Peak: 11.8, GridImpact: 7.8 },
  { SOC: 85.6, Peak: 87.2, GridImpact: 43.6 },
  { SOC: 78.6, Peak: 85.8, GridImpact: 42.9 },
  { SOC: 75, Peak: 19.5, GridImpact: 14.2 },
  { SOC: 70.8, Peak: 11.6, GridImpact: 7.9 },
  { SOC: 68.3, Peak: 71.8, GridImpact: 35.9 },
  { SOC: 62.5, Peak: 16.8, GridImpact: 11.4 },
  { SOC: 56.7, Peak: 17.2, GridImpact: 12.1 },
  { SOC: 54.2, Peak: 11.9, GridImpact: 8.2 },
  { SOC: 50, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 50, Peak: 7.4, GridImpact: 7.4 },
  { SOC: 50, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 50, Peak: 7.1, GridImpact: 7.1 },
  { SOC: 50, Peak: 7.3, GridImpact: 7.3 },
  { SOC: 53.1, Peak: 6.7, GridImpact: 6.7 },
  { SOC: 56.3, Peak: 7.5, GridImpact: 7.5 },
  { SOC: 59.4, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 62.5, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 65.6, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 67.6, Peak: 7.4, GridImpact: 7.4 },
  { SOC: 71.9, Peak: 6.6, GridImpact: 6.6 },
  { SOC: 75, Peak: 7.1, GridImpact: 7.1 },
  { SOC: 78.1, Peak: 7.6, GridImpact: 7.6 },
  { SOC: 76.3, Peak: 14.8, GridImpact: 14.8 },
  { SOC: 77.7, Peak: 7.3, GridImpact: 7.3 },
  { SOC: 88.6, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 90.6, Peak: 7.5, GridImpact: 7.5 },
  { SOC: 92.6, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 96.9, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 100, Peak: 6.7, GridImpact: 6.7 },
  { SOC: 100, Peak: 7.4, GridImpact: 7.4 },
  { SOC: 100, Peak: 7.1, GridImpact: 7.1 },
  { SOC: 100, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 100, Peak: 7.6, GridImpact: 7.6 },
  { SOC: 100, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 97.8, Peak: 13.7, GridImpact: 13.7 },
  { SOC: 99.4, Peak: 7.8, GridImpact: 7.8 },
  { SOC: 100, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 100, Peak: 6.5, GridImpact: 6.5 },
  { SOC: 100, Peak: 7.3, GridImpact: 7.3 },
  { SOC: 100, Peak: 8.1, GridImpact: 8.1 },
  { SOC: 100, Peak: 11.8, GridImpact: 7.9 },
  { SOC: 97.5, Peak: 14.7, GridImpact: 9.8 },
  { SOC: 95, Peak: 10.9, GridImpact: 7.6 },
  { SOC: 92.5, Peak: 13.8, GridImpact: 9.2 },
  { SOC: 89.4, Peak: 15.9, GridImpact: 11.3 },
  { SOC: 87.5, Peak: 94.3, GridImpact: 47.2 },
  { SOC: 82.8, Peak: 12.7, GridImpact: 8.1 },
  { SOC: 82.5, Peak: 89.6, GridImpact: 44.8 },
  { SOC: 78.9, Peak: 85.4, GridImpact: 42.7 },
  { SOC: 77.5, Peak: 15.8, GridImpact: 11.6 },
  { SOC: 76.7, Peak: 36.5, GridImpact: 18.3 },
  { SOC: 72.5, Peak: 26.8, GridImpact: 15.4 },
  { SOC: 67.2, Peak: 11.7, GridImpact: 7.9 },
  { SOC: 67.5, Peak: 64.2, GridImpact: 32.1 },
  { SOC: 66.1, Peak: 13.6, GridImpact: 9.8 },
  { SOC: 63.1, Peak: 71.5, GridImpact: 35.8 },
  { SOC: 59.4, Peak: 55.3, GridImpact: 27.7 },
  { SOC: 57.5, Peak: 20.8, GridImpact: 13.4 },
  { SOC: 57.2, Peak: 11.9, GridImpact: 7.8 },
  { SOC: 52.5, Peak: 7.3, GridImpact: 7.3 },
  { SOC: 50, Peak: 6.8, GridImpact: 6.8 },
  { SOC: 50, Peak: 7.2, GridImpact: 7.2 },
  { SOC: 50, Peak: 6.9, GridImpact: 6.9 },
  { SOC: 50, Peak: 7.4, GridImpact: 7.4 },
  { SOC: 50, Peak: 6.7, GridImpact: 6.7 },
  { SOC: 50, Peak: 7.1, GridImpact: 7.1 },
  { SOC: 50, Peak: 12.8, GridImpact: 12.8 },
  { SOC: 50, Peak: 7.5, GridImpact: 7.5 },
];

const W = 1000;
const H = 600;
const TOP = 70; // headroom above the 100% line
const BASE = 515; // zero baseline; space below is reserved for the HTML axis
const MAX = 100; // shared value domain (SOC tops out at 100)

const N = DATA.length;
const xAt = (i: number) => (i / (N - 1)) * W;
const yAt = (v: number) => BASE - (v / MAX) * (BASE - TOP);

const SOC_COLOR = "#22c55e";
const PEAK_COLOR = "#f04438";
const GRID_COLOR = "#4d8dff";

// Monotone cubic interpolation (Fritsch–Carlson): smooth, no overshoot.
function monotonePath(values: number[]): string {
  const xs = values.map((_, i) => xAt(i));
  const ys = values.map((v) => yAt(v));
  const n = values.length;
  const delta: number[] = [];
  for (let i = 0; i < n - 1; i += 1) delta[i] = (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]);
  const m: number[] = new Array(n);
  m[0] = delta[0];
  m[n - 1] = delta[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    m[i] = delta[i - 1] * delta[i] <= 0 ? 0 : (delta[i - 1] + delta[i]) / 2;
  }
  for (let i = 0; i < n - 1; i += 1) {
    if (delta[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / delta[i];
    const b = m[i + 1] / delta[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * delta[i];
      m[i + 1] = t * b * delta[i];
    }
  }
  let d = `M${xs[0].toFixed(1)} ${ys[0].toFixed(1)}`;
  for (let i = 0; i < n - 1; i += 1) {
    const h = xs[i + 1] - xs[i];
    const c1x = xs[i] + h / 3;
    const c1y = ys[i] + (m[i] * h) / 3;
    const c2x = xs[i + 1] - h / 3;
    const c2y = ys[i + 1] - (m[i + 1] * h) / 3;
    d += `C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${xs[i + 1].toFixed(1)} ${ys[i + 1].toFixed(1)}`;
  }
  return d;
}

const close = (line: string) => `${line}L${W} ${BASE}L0 ${BASE}Z`;

const SOC_LINE = monotonePath(DATA.map((d) => d.SOC));
const PEAK_LINE = monotonePath(DATA.map((d) => d.Peak));
const GRID_LINE = monotonePath(DATA.map((d) => d.GridImpact));

const TICKS = [3, 6, 9, 12, 15, 18, 21];
const tickLeft = (h: number) => `${((h * 4) / (N - 1)) * 100}%`;

const STROKE = {
  strokeWidth: 1.4,
  strokeLinejoin: "round" as const,
  strokeLinecap: "round" as const,
  vectorEffect: "non-scaling-stroke" as const,
  fill: "none",
};

function ChartLayer({ kind }: { kind: "before" | "after" }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
      {kind === "before" ? (
        <>
          <defs>
            <linearGradient id="pc-peak" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={PEAK_COLOR} stopOpacity="0.2" />
              <stop offset="100%" stopColor={PEAK_COLOR} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={close(PEAK_LINE)} fill="url(#pc-peak)" />
          <path d={PEAK_LINE} stroke={PEAK_COLOR} {...STROKE} />
        </>
      ) : (
        <>
          <defs>
            <linearGradient id="pc-grid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={GRID_COLOR} stopOpacity="0.18" />
              <stop offset="100%" stopColor={GRID_COLOR} stopOpacity="0" />
            </linearGradient>
            <linearGradient id="pc-soc" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SOC_COLOR} stopOpacity="0.22" />
              <stop offset="100%" stopColor={SOC_COLOR} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={close(GRID_LINE)} fill="url(#pc-grid)" />
          <path d={GRID_LINE} stroke={GRID_COLOR} {...STROKE} />
          <path d={close(SOC_LINE)} fill="url(#pc-soc)" />
          <path d={SOC_LINE} stroke={SOC_COLOR} {...STROKE} />
        </>
      )}
    </svg>
  );
}

export function PeakChart({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // Divider position in %. Starts centred so both sides are visible at rest.
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const setFromX = (clientX: number) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  const startDrag = (e: React.PointerEvent) => {
    // Keep the wrapping card link from navigating / selecting while we drag.
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    setFromX(e.clientX);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (dragging) setFromX(e.clientX);
  };
  const endDrag = () => setDragging(false);

  // Snap 1:1 to the pointer while dragging; ease a click-to-jump otherwise.
  const clipTransition = dragging ? "none" : "clip-path 250ms cubic-bezier(0.22,1,0.36,1)";

  return (
    <div
      ref={ref}
      className={`relative cursor-ew-resize select-none ${className ?? ""}`}
      style={{ touchAction: "pan-y" }}
      onPointerDown={startDrag}
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* After (green + blue) — to the right of the divider */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 0 0 ${pos}%)`, transition: clipTransition }}
      >
        <ChartLayer kind="after" />
      </div>
      {/* Before (red) — to the left of the divider */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)`, transition: clipTransition }}
      >
        <ChartLayer kind="before" />
      </div>

      {/* Divider + drag handle — always visible */}
      <div
        className="pointer-events-none absolute inset-y-0 z-10 -ml-px w-0.5 bg-white/35"
        style={{
          left: `${pos}%`,
          transition: dragging ? "none" : "left 250ms cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div className="absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-white/15 text-white shadow-[0_2px_10px_rgba(0,0,0,0.5)] backdrop-blur-md transition-transform duration-200 group-hover:scale-110">
          <ChevronsLeftRight size={14} strokeWidth={2} />
        </div>
      </div>

      {/* Time axis (HTML so labels stay crisp despite the stretched SVG) */}
      <div className="pointer-events-none absolute inset-x-0 bottom-3 h-4 text-xs font-medium tracking-tight text-white/35">
        {TICKS.map((h) => (
          <span key={h} className="absolute -translate-x-1/2" style={{ left: tickLeft(h) }}>
            {String(h).padStart(2, "0")}:00
          </span>
        ))}
      </div>
    </div>
  );
}
